import { useState } from 'react'
import { ArrowRight, Eye, EyeOff, LockKeyhole, Mail, User } from 'lucide-react'

import { logoIconUrl } from '../assets/clinicAssets'
import { API_URL } from '../constants/app'
import { formatApiError, formatRequestError, readErrorPayload } from '../utils/errors'

export default function AuthPage({ mode, onAuthenticate, onModeChange }) {
  const isRegister = mode === 'register'
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    email: '',
    name: '',
    password: '',
    remember: true,
  })

  function updateAuthForm(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }))
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setIsSubmitting(true)
    setError('')

    const path = isRegister ? '/auth/register' : '/auth/login'
    const body = isRegister
      ? {
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
        }
      : {
          email: form.email.trim(),
          password: form.password,
          remember: form.remember,
        }

    try {
      let response
      try {
        response = await fetch(`${API_URL}${path}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        })
      } catch (networkError) {
        throw new Error(formatRequestError(networkError))
      }

      const payload = await readErrorPayload(response)
      if (!response.ok) {
        throw new Error(
          formatApiError(
            payload,
            isRegister ? 'Não foi possível criar a conta.' : 'Não foi possível entrar.',
          ),
        )
      }

      onAuthenticate(
        {
          access_token: payload.access_token,
          token_type: payload.token_type || 'bearer',
          user: payload.user,
        },
        isRegister ? true : form.remember,
      )
    } catch (err) {
      setError(formatRequestError(err, 'Não foi possível concluir a autenticação.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#F8E1E7] px-4 py-5 text-[#1A1A1A]">
      <div className="relative flex min-h-[560px] w-full max-w-[440px] items-center justify-center px-0 py-6">
        <section className="relative w-full max-w-[340px] rounded-xl bg-white px-5 py-6 shadow-[0_18px_44px_rgba(26,26,26,0.10)] sm:px-6">
          <div className="mb-5 text-center">
            <img
              alt="Dra. Gleice Gomes"
              className="mx-auto mb-4 h-12 w-16 object-contain"
              src={logoIconUrl}
            />
            <h1 className="text-[28px] font-bold leading-tight text-[#1A1A1A]">
              {isRegister ? 'Criar Conta' : 'Login'}
            </h1>
            <p className="mx-auto mt-2 max-w-[280px] text-sm font-medium leading-5 text-[#5A5147]">
              {isRegister ? 'Faça seu cadastro.' : 'Insira suas credenciais para continuar.'}
            </p>
          </div>

          {error ? (
            <div className="mb-3.5 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700">
              {error}
            </div>
          ) : null}

          <form className="grid gap-3.5" onSubmit={handleSubmit}>
            {isRegister ? (
              <AuthField icon={User} label="Nome completo">
                <input
                  className="auth-input"
                  minLength="2"
                  placeholder="Seu nome completo"
                  required
                  value={form.name}
                  onChange={(event) => updateAuthForm('name', event.target.value)}
                />
              </AuthField>
            ) : null}

            <AuthField icon={Mail} label="E-mail">
              <input
                className="auth-input"
                placeholder="seu@email.com"
                required
                type="email"
                value={form.email}
                onChange={(event) => updateAuthForm('email', event.target.value)}
              />
            </AuthField>

            <AuthField icon={LockKeyhole} label="Senha">
              <input
                className="auth-input auth-input-password"
                minLength="8"
                placeholder="••••••••"
                required
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(event) => updateAuthForm('password', event.target.value)}
              />
              <button
                className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-[#BCAFA0] transition hover:bg-[#FBF8F4] hover:text-[#5A5147]"
                type="button"
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                onClick={() => setShowPassword((current) => !current)}
              >
                {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </AuthField>

            {!isRegister ? (
              <label className="flex items-center gap-2.5 text-sm font-medium text-[#5A5147]">
                <input
                  className="h-4 w-4 rounded border-[#D6CDBF] accent-[#1A1A1A]"
                  type="checkbox"
                  checked={form.remember}
                  onChange={(event) => updateAuthForm('remember', event.target.checked)}
                />
                Manter conectado
              </label>
            ) : null}

            <button
              className="mt-1 flex h-11 items-center justify-center gap-3 rounded-md bg-[#1A1A1A] text-sm font-bold uppercase tracking-[0.03em] text-white shadow-[0_12px_24px_rgba(26,26,26,0.14)] transition hover:bg-[#2C2A28] disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting
                ? isRegister
                  ? 'Cadastrando...'
                  : 'Entrando...'
                : isRegister
                  ? 'Cadastrar'
                  : 'Entrar'}
              {!isSubmitting ? <ArrowRight size={20} strokeWidth={2.4} aria-hidden="true" /> : null}
            </button>
          </form>

          <div className="mt-6 text-center">
            {!isRegister ? (
              <>
                <div className="mb-3 flex items-center gap-3">
                  <div className="h-px flex-1 bg-[#EEE8DF]" />
                  <span className="text-sm font-medium text-[#8D857C]">Novo por aqui?</span>
                  <div className="h-px flex-1 bg-[#EEE8DF]" />
                </div>
                <button
                  className="rounded-md border border-[#D6CDBF] bg-white px-4 py-1.5 text-sm font-bold text-[#8A6819] transition hover:bg-[#FBF8F4]"
                  type="button"
                  onClick={() => {
                    setError('')
                    onModeChange('register')
                  }}
                >
                  Criar uma conta
                </button>
              </>
            ) : (
              <p className="text-sm font-medium text-[#5A5147]">
                Já possui uma conta?{' '}
                <button
                  className="font-bold text-[#8A6819]"
                  type="button"
                  onClick={() => {
                    setError('')
                    onModeChange('login')
                  }}
                >
                  Faça login
                </button>
              </p>
            )}
          </div>
        </section>
      </div>
    </main>
  )
}

function AuthField({ action, children, icon: Icon, label }) {
  return (
    <label className="grid gap-2">
      <span className="flex items-center justify-between gap-3 text-sm font-bold uppercase tracking-[0.04em] text-[#5A5147]">
        <span>{label}</span>
        {action}
      </span>
      <span className="relative block">
        <Icon
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#BCAFA0]"
          size={22}
          strokeWidth={2.2}
          aria-hidden="true"
        />
        {children}
      </span>
    </label>
  )
}
